/* Stock report — yarn on hand, or waste, count by count.
 *
 * Four figures, the closing total as a line over the month, then the
 * register itself. The line's band is the month's range with a little air,
 * not zero-based: a stock level that moves 5% over a month is a flat line
 * against a zero axis and says nothing.
 */

import type { MobileSummary } from '../../lib/api_mobile'
import { MobileTable, type MRow } from '../MobileTable'
import {
  Head, IN, KpiGrid, LineChart, NoteBox, P1, SG, Seg, TN, kpi,
} from '../parts'

export type StockTab = 'yarn' | 'waste'

export function Stock({ data, tab, onTab }: {
  data: MobileSummary
  tab: StockTab
  onTab: (t: StockTab) => void
}) {
  const st = data.stock
  const days = st.days
  const last = days[days.length - 1]
  const first = days[0]
  const short = st.label.slice(0, 3)

  const prod = days.reduce((s, d) => s + d.prod, 0)
  const disp = days.reduce((s, d) => s + d.disp, 0)

  const seg = (
    <Seg value={tab} onChange={onTab}
      options={[{ v: 'yarn', l: 'Yarn' }, { v: 'waste', l: 'Waste' }]} />
  )
  const period = (
    <div className="m-label" style={{ padding: '12px 0 2px' }}>
      {st.label} · closing {st.last ? `${last.d} ${short}` : '—'} · day tracker
    </div>
  )

  if (!last) {
    return <>{seg}{period}<NoteBox>No stock register on file for this month.</NoteBox></>
  }

  const series = days.map((d) => (tab === 'yarn' ? d.yarn : d.waste))
  const lo = Math.min(...series) * 0.95
  const hi = Math.max(...series) * 1.02

  return (
    <>
      {seg}
      {period}

      {tab === 'yarn' ? (
        <KpiGrid style={{ marginTop: 16 }} items={[
          kpi('Yarn on hand', TN(last.yarn), `${SG(last.yarn - first.yarn, TN)} since ${first.d} ${short}`),
          kpi('Days of cover', st.cover == null ? '–' : String(st.cover),
            st.cover == null ? 'nothing dispatched yet' : 'at this month\'s average dispatch'),
          kpi(`Produced · ${short}`, TN(prod), `${TN(prod / days.length)} a day`),
          kpi(`Dispatched · ${short}`, TN(disp),
            disp ? `${TN(disp / days.length)} a day` : 'nothing dispatched yet', !disp),
        ]} />
      ) : (
        <KpiGrid style={{ marginTop: 16 }} items={[
          kpi('Waste on hand', TN(last.waste),
            `${SG(last.waste - first.waste, TN)} since ${first.d} ${short}`,
            last.waste > first.waste),
          kpi(`Generated · ${short}`, TN(st.waste.reduce((s, w) => s + w.prod, 0)),
            prod ? `${P1(st.waste.reduce((s, w) => s + w.prod, 0) / prod * 100)} of yarn made` : ''),
          kpi(`Sold · ${short}`, TN(st.waste.reduce((s, w) => s + w.sold, 0)),
            st.waste.some((w) => w.sold) ? 'cleared from stock' : 'nothing cleared yet',
            !st.waste.some((w) => w.sold)),
          kpi('Types held', String(st.waste.filter((w) => w.close > 0).length),
            'with stock on hand'),
        ]} />
      )}

      <Head title={tab === 'yarn' ? 'Yarn closing stock' : 'Waste closing stock'}
        aside="tonnes" />
      <LineChart series={series} lo={lo} hi={hi}
        fmt={(v) => (v / 1000).toFixed(0)}
        from={`${first.d} ${short}`} to={`${last.d} ${short}`}
        legend={[{ t: 'Closing stock', border: '2px solid var(--c-ink)', line: true }]} />

      {tab === 'yarn' ? <ByCount data={data} /> : <ByType data={data} />}
    </>
  )
}

function ByCount({ data }: { data: MobileSummary }) {
  const rows = data.stock.yarn
  const maxC = Math.max(1, ...rows.map((y) => y.close))
  const short = data.stock.label.slice(0, 3)
  const day = data.stock.days[data.stock.days.length - 1]
  const table: MRow[] = rows.map((y) => ({
    cells: [
      { v: y.c },
      { v: IN(y.close), bar: y.close / maxC * 100, s: y.close },
      { v: IN(y.open) }, { v: IN(y.prod) }, { v: IN(y.disp) },
      { v: IN(y.mprod) }, { v: IN(y.mdisp) },
    ],
  }))
  return (
    <>
      <Head title="By count" aside={`${day.d} ${short}`} />
      <MobileTable cols={[
        { l: 'Count', w: 86 }, { l: 'Closing', w: 116 }, { l: 'Opening', w: 78 },
        { l: 'Made', w: 64 }, { l: 'Sent', w: 64 },
        { l: `${short} made`, w: 78 }, { l: `${short} sent`, w: 78 },
      ]} rows={table} />
      <div className="m-aside" style={{ marginTop: 8 }}>
        Made, sent and opening are the last day's; the last two columns are
        the whole month's.
      </div>
    </>
  )
}

function ByType({ data }: { data: MobileSummary }) {
  const rows = data.stock.waste
  const maxC = Math.max(1, ...rows.map((w) => w.close))
  const short = data.stock.label.slice(0, 3)
  const day = data.stock.days[data.stock.days.length - 1]
  const table: MRow[] = rows.map((w) => ({
    cells: [
      { v: w.type },
      { v: IN(w.close), bar: w.close / maxC * 100, s: w.close },
      { v: w.dept },
      { v: IN(w.prod) },
      { v: IN(w.sold) },
    ],
  }))
  return (
    <>
      <Head title="By waste type" aside={`${day.d} ${short}`} />
      <MobileTable cols={[
        { l: 'Type', w: 96 }, { l: 'Closing', w: 116 },
        { l: 'Department', w: 110, al: 'left' },
        { l: `${short} made`, w: 76 }, { l: `${short} sold`, w: 76 },
      ]} rows={table} />
    </>
  )
}
